<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between mb-2">
      <h2 class="text-2xl font-bold text-vault-text-primary">
        {{ $t('walletDeposit.title') }}
      </h2>
      <button
        type="button"
        class="btn-ghost px-3 py-2 text-sm"
        :disabled="loading"
        @click="handleCancel"
      >
        ← {{ $t('common.back') }}
      </button>
    </div>

    <p class="text-sm text-vault-text-secondary mb-4">
      {{ $t('walletDeposit.subtitle') }}
    </p>

    <!-- Deposit Method Selection -->
    <div v-if="currentView === 'select'" class="flex flex-col gap-3">
      <button
        type="button"
        class="btn-primary flex items-center justify-center gap-2"
        :disabled="loading"
        @click="selectMethod('card')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <rect width="20" height="14" x="2" y="5" rx="2"/>
          <line x1="2" x2="22" y1="10" y2="10"/>
        </svg>
        {{ $t('walletDeposit.buyWithCard') }}
      </button>

      <button
        type="button"
        class="btn-secondary flex items-center justify-center gap-2"
        :disabled="loading"
        @click="selectMethod('crypto')"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
        </svg>
        {{ $t('walletDeposit.buyWithCrypto') }}
      </button>
    </div>

    <!-- MoonPay Widget -->
    <WalletMoonPayWidget
      v-else-if="currentView === 'moonpay'"
      :address="address"
      :currency="currency"
      :currency-info="currencyInfo"
      @back="currentView = 'select'"
      @transaction-started="handleTransactionStarted"
      @transaction-completed="handleTransactionCompleted"
    />

    <!-- Currency Selector -->
    <CompactCurrencySelector
      v-else-if="currentView === 'currencySelect'"
      :wallet-address="address"
      :wallet-currency="currencyInfo || { id: currency, name: currency, code: currency, symbol: currency, type: 'erc20', decimals: 6 }"
      :wallet-network="{ id: chain || 'base_sepolia', name: 'Base Sepolia' }"
      @select="handleCurrencySelected"
    />

    <!-- Changelly Widget -->
    <WalletChangellyWidget
      v-else-if="currentView === 'changelly'"
      :address="address"
      :currency="currency"
      :currency-info="currencyInfo"
      :from-currency-selected="selectedCurrency"
      :from-network-selected="selectedNetwork"
      @back="currentView = 'select'"
      @change-currency="handleChangeCurrency"
      @transaction-started="handleTransactionStarted"
      @transaction-completed="handleTransactionCompleted"
    />

    <!-- Deposit Monitoring -->
    <DepositStatusMonitor
      v-else-if="currentView === 'monitoring'"
      :address="address"
      :currency="currency"
      :currency-info="currencyInfo"
      :rpc-url="rpcUrl"
      :chain="chain"
      :auto-start="true"
      @deposit-received="handleDepositReceived"
      @timeout="handleMonitoringTimeout"
      @error="handleMonitoringError"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import WalletMoonPayWidget from './WalletMoonPayWidget.vue';
import WalletChangellyWidget from './WalletChangellyWidget.vue';
import DepositStatusMonitor from './DepositStatusMonitor.vue';
import CompactCurrencySelector from './CompactCurrencySelector.vue';

const { t: $t } = useI18n();

interface CurrencyInfo {
  id: string;
  name: string;
  code: string;
  symbol: string;
  type: 'native' | 'erc20';
  decimals: number;
}

const props = defineProps<{
  address: string;
  currency: string;
  currencyInfo?: CurrencyInfo;
  loading?: boolean;
  rpcUrl?: string;
  chain?: string;
}>();

const emit = defineEmits<{
  cancel: [];
  'deposit-received': [balance: any];
  'transaction-started': [data: any];
  'transaction-completed': [data: any];
}>();

type DepositView = 'select' | 'card' | 'crypto' | 'moonpay' | 'changelly' | 'monitoring' | 'currencySelect';

const currentView = ref<DepositView>('select');
const selectedCurrency = ref<any>(null);
const selectedNetwork = ref<any>(null);

function selectMethod(method: 'card' | 'crypto') {
  if (method === 'card') {
    currentView.value = 'moonpay';
  } else {
    // First show currency selector for crypto
    currentView.value = 'currencySelect';
  }
}

function handleCurrencySelected(option: any) {
  console.log('Currency selected:', option);
  selectedCurrency.value = option.currency;
  selectedNetwork.value = option.network;
  currentView.value = 'changelly';
}

function handleChangeCurrency() {
  currentView.value = 'currencySelect';
}

function handleCancel() {
  if (currentView.value === 'select') {
    emit('cancel');
  } else if (currentView.value === 'currencySelect' || currentView.value === 'changelly') {
    currentView.value = 'select';
    selectedCurrency.value = null;
    selectedNetwork.value = null;
  } else {
    currentView.value = 'select';
  }
}

function handleTransactionStarted(data: any) {
  console.log('🔄 [WalletDeposit] Transaction started:', data);
  emit('transaction-started', data);
}

function handleTransactionCompleted(data: any) {
  console.log('✅ [WalletDeposit] Transaction completed:', data);
  emit('transaction-completed', data);
  // Switch to monitoring view
  currentView.value = 'monitoring';
}

function handleDepositReceived(balance: any) {
  console.log('✅ [WalletDeposit] Deposit received:', balance);
  emit('deposit-received', balance);
}

function handleMonitoringTimeout() {
  console.warn('⏰ [WalletDeposit] Monitoring timed out');
  currentView.value = 'select';
}

function handleMonitoringError(err: Error) {
  console.error('❌ [WalletDeposit] Monitoring error:', err);
  currentView.value = 'select';
}
</script>

<style scoped>
/* All styles handled by Tailwind */
</style>
